import React, { createContext, useContext, useEffect, useId, useRef, useState } from "react";
import { T } from "../app/tokens.js";
import { useSpacePolicy } from "../services/spacePolicyStore.js";
import { fitPolicy } from "../services/spacePolicyValues.js";
export const SpacePolicyRoleContext = createContext("viewer");
export function SpacePolicyDefinitions({ policy, anchor = true, fitControls, outlookControls }) {
  const section = useRef(null);
  const ready = Boolean(policy);
  useEffect(() => {
    if (anchor && window.location.hash === "#definitions" && ready) section.current?.scrollIntoView?.({ block: "start" });
  }, [anchor, ready]);
  if (!policy) return <p>Space Policy unavailable. Definitions require the effective company policy.</p>;
  if (!fitPolicy(policy)) return <section ref={section} id={anchor ? "definitions" : undefined} role="alert">
    Incoherent Space Policy: Under-Used below must be less than Tight from, below 100%,
    and the density floor must be positive. Correct the company policy before scoring.
  </section>;
  const { tightPct: t, nearLimitPct: near } = fitPolicy(policy);
  return <section ref={section} id={anchor ? "definitions" : undefined} aria-label="Definitions" data-testid="space-policy-definitions" style={{ color: T.dark, background: T.card }}>
    <h2>Definitions</h2>
    <article style={{ background: T.card, border: `1px solid ${T.border}`, padding: 16, marginBottom: 12 }}>
      <h3>SPACE FIT (today)</h3>
      <p>Assigned today ÷ installed seats, per office; a market is the sum of its offices. Only operational and dormant offices are scored. SF per seat below the floor shows amber.</p>
      <p>Under-Used below {policy.underUsedPct}%, Right-Sized {policy.underUsedPct}% to below {t}%,
        Tight {t}% to 100%. Over-Cap fixed above 100% (not editable). Floor {policy.sfFloor} sf per seat.</p>
      {fitControls}
      <span style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: "2px 8px" }}>Used by: Space Fit (Offices, Markets, Reports)</span>
    </article>
    <article style={{ background: T.card, border: `1px solid ${T.border}`, padding: 16 }}>
      <h3>CAPACITY OUTLOOK (planning)</h3>
      <p>Forecast demand ÷ usable seats. Usable seats = installed seats adjusted by the utilization
        assumptions below (days in office, desk sharing, hoteling hold, conf-room giveback, team-block waste).
        Room below {near}%, Near limit {near}% to 100%.</p>
      <p>This month uses assigned today; forecast demand starts next month. Over limit fixed above 100% (not editable). Under-Used does not add an Outlook band. Target buffer remains a separate planning assumption.</p>
      {outlookControls}
      <span style={{ border: `1px solid ${T.border}`, borderRadius: 12, padding: "2px 8px" }}>Used by: Capacity planning, Utilization</span>
    </article>
  </section>;
}
export function HowWeDefineThis() {
  const role = useContext(SpacePolicyRoleContext);
  const { baseline, status, error } = useSpacePolicy();
  const [open, setOpen] = useState(false);
  const id = useId();
  const button = useRef(null);
  if (role === "admin") return <a href="/admin?section=space_policy#definitions" onClick={e => e.stopPropagation()}>How we define this →</a>;
  return <div style={{ pointerEvents: "auto" }} onKeyDown={e => {
    if (e.key === "Escape") { e.stopPropagation(); setOpen(false); button.current?.focus({ preventScroll: true }); }
  }}>
    <button ref={button} type="button" aria-expanded={open} aria-controls={id} onClick={e => { e.stopPropagation(); setOpen(!open); }}>
      How we define this →
    </button>
    {open && <div id={id} role="region" tabIndex={0} aria-label="Read-only Space Policy definitions" onClick={e => e.stopPropagation()}
      style={{ position: "fixed", top: 72, right: 16, zIndex: 9999, width: "min(640px, calc(100vw - 32px))",
        maxHeight: "calc(100vh - 96px)", overflow: "auto", background: T.card, color: T.dark,
        border: `1px solid ${T.border}`, borderRadius: 8, padding: 16, pointerEvents: "auto" }}>
      {status === "loading" ? <p>Loading Space Policy…</p> : error ? <p role="alert">{error}</p>
        : <SpacePolicyDefinitions policy={baseline} anchor={false} />}
    </div>}
  </div>;
}
